import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Users, Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Scale, Flag, Building, MapPin, Calendar, Activity, Gavel, DollarSign, Search, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ESCAVADOR_API_URL, escavadorHeaders } from '../config/api';
import { registrarBilling } from '../services/billing';
import ModalEnriquecimento from './ModalEnriquecimento';

// Helpers para mapear campos do formato Escavador
const getMainFonte = (processo) =>
  processo?.fontes?.find(f => f.tipo === 'TRIBUNAL') || processo?.fontes?.[0];

const getAllEnvolvidos = (processo) => {
  if (!processo?.fontes) return [];
  const seen = new Set();
  const result = [];
  processo.fontes.forEach(fonte => {
    (fonte.envolvidos || []).forEach(e => {
      if (!seen.has(e.nome)) {
        seen.add(e.nome);
        result.push({
          nome: e.nome,
          tipo: e.tipo_normalizado || e.tipo || '-',
          polo: e.polo || 'DESCONHECIDO',
          cpf_cnpj: e.cpf || e.cnpj || null,
          oabs: e.oabs || [],
          advogados: e.advogados || [],
          quantidade_processos: e.quantidade_processos,
        });
      }
    });
  });
  return result;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
};

export default function DetalhesProcesso() {
  const { darkMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processo = location.state?.processo;

  const [movimentacoesLoading, setMovimentacoesLoading] = useState(false);
  const [movimentacoesLoadingMore, setMovimentacoesLoadingMore] = useState(false);
  const [movimentacoes, setMovimentacoes] = useState(null);
  const [mostrarMovimentacoes, setMostrarMovimentacoes] = useState(false);
  const [movimentacaoPageSize, setMovimentacaoPageSize] = useState(10);
  const [movimentacaoCurrentPage, setMovimentacaoCurrentPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [envolvidoSelecionado, setEnvolvidoSelecionado] = useState(null);
  const [dadosEnriquecidos, setDadosEnriquecidos] = useState(null);
  const [enriquecimentoLoading, setEnriquecimentoLoading] = useState(false);
  const [enriquecimentoError, setEnriquecimentoError] = useState(null);

  useEffect(() => {
    if (!processo) navigate('/busca-processual');
  }, [processo, navigate]);

  if (!processo) return null;

  // Extrair campos do formato Escavador
  const mainFonte = getMainFonte(processo);
  const capa = mainFonte?.capa;
  const envolvidos = getAllEnvolvidos(processo);

  const tribunal = processo.unidade_origem?.tribunal_sigla || mainFonte?.sigla || '-';
  const uf = processo.estado_origem?.sigla || '-';
  const comarca = processo.unidade_origem?.cidade || '-';
  const orgaoJulgador = capa?.orgao_julgador || processo.unidade_origem?.nome || '-';
  const classe = capa?.classe || '-';
  const assunto = capa?.assunto || '-';
  const area = capa?.area || '-';
  const valorFormatado = capa?.valor_causa?.valor_formatado || '-';
  const isAtivo = !processo.fontes_tribunais_estao_arquivadas;
  const grau = mainFonte?.grau_formatado || '-';

  // Polo da empresa pesquisada (do primeiro tipo de envolvido pesquisado)
  const tipoEnvolvidoPesquisado = mainFonte?.tipos_envolvido_pesquisado?.[0];
  const polo = tipoEnvolvidoPesquisado?.polo || '-';
  const tipoEnvolvimento = tipoEnvolvidoPesquisado?.tipo_normalizado || tipoEnvolvidoPesquisado?.tipo || '-';

  // Juiz (procurar nos envolvidos)
  const juiz = envolvidos.find(e => e.tipo?.toLowerCase().includes('juiz') || e.tipo?.toLowerCase().includes('relator'))?.nome || '-';

  const isCPF = (cpfCnpj) => cpfCnpj?.replace(/\D/g, '')?.length === 11;

  const obterEnriquecimento = async (envolvido) => {
    setModalOpen(true);
    setEnvolvidoSelecionado(envolvido);
    setEnriquecimentoLoading(true);
    setEnriquecimentoError(null);
    setDadosEnriquecidos(null);

    try {
      if (!envolvido.cpf_cnpj) throw new Error('CPF/CNPJ não disponível');

      const url = `${ESCAVADOR_API_URL}/envolvido/processos?cpf_cnpj=${encodeURIComponent(envolvido.cpf_cnpj)}`;
      const response = await fetch(url, { headers: escavadorHeaders() });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setDadosEnriquecidos(data);

      const digits = String(envolvido.cpf_cnpj).replace(/\D/g, '');
      registrarBilling({
        route: 'api/v2/envolvido/processos/cpf_cnpj={cpf_cnj}',
        path: `api/v2/envolvido/processos/cpf_cnpj=${envolvido.cpf_cnpj}`,
        value: 0.50,
        param: digits,
        type: digits.length === 14 ? 'cnpj' : 'cpf',
      });
    } catch (error) {
      setEnriquecimentoError(error.message);
    } finally {
      setEnriquecimentoLoading(false);
    }
  };

  const fetchMovimentacoes = async (url, append = false) => {
    try {
      const response = await fetch(url, { headers: escavadorHeaders() });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const novos = data.items || [];
      const nextLink = data.links?.next || null;

      setMovimentacoes(prev => ({
        movimentacoes: append ? [...(prev?.movimentacoes || []), ...novos] : novos,
        nextLink,
      }));

      const pathMatch = url.match(/api\/v2\/.+/);
      const billingPath = pathMatch ? pathMatch[0].split('?')[0] : url;
      registrarBilling({
        route: 'api/v2/processos/numero_cnj/{cnj}/movimentacoes',
        path: billingPath,
        value: 0.05,
        param: processo.numero_cnj,
        type: 'cnj',
      });

      if (!append) {
        setMovimentacaoCurrentPage(1);
        setMostrarMovimentacoes(true);
      }
    } catch (error) {
      console.error('❌ Erro movimentações:', error);
      alert(`Erro ao buscar movimentações: ${error.message}`);
    }
  };

  const handleVerMovimentacoes = async () => {
    if (mostrarMovimentacoes) {
      setMostrarMovimentacoes(false);
      return;
    }

    if (!processo.numero_cnj) {
      alert('Número CNJ não disponível para buscar movimentações');
      return;
    }

    try {
      setMovimentacoesLoading(true);
      const cnj = processo.numero_cnj.replace(/\D/g, '');
      const url = `${ESCAVADOR_API_URL}/processos/numero_cnj/${encodeURIComponent(cnj)}/movimentacoes`;
      console.log('🔍 Movimentações:', url);
      await fetchMovimentacoes(url, false);
    } finally {
      setMovimentacoesLoading(false);
    }
  };

  const handleCarregarMaisMovimentacoes = async () => {
    if (!movimentacoes?.nextLink) return;
    try {
      setMovimentacoesLoadingMore(true);
      await fetchMovimentacoes(movimentacoes.nextLink, true);
    } finally {
      setMovimentacoesLoadingMore(false);
    }
  };

  const infoItem = (icon, label, value, colorClass = '') => (
    <div className={`py-5 border-b ${darkMode ? 'border-neutral-700' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        {React.cloneElement(icon, { className: `w-4 h-4 ${colorClass || (darkMode ? 'text-neutral-400' : 'text-gray-400')}` })}
        <p className={`text-xs font-semibold ${darkMode ? 'text-neutral-400' : 'text-gray-600'} uppercase`}>{label}</p>
      </div>
      <div className="ml-6">{value}</div>
    </div>
  );

  const textVal = (val, extraClass = '') => (
    <p className={`text-sm font-medium ${extraClass || (darkMode ? 'text-white' : 'text-gray-900')}`}>{val}</p>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-neutral-800 text-neutral-400' : 'hover:bg-gray-100 text-gray-600'}`}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className={`text-2xl font-bold font-mono ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {processo.numero_cnj}
          </h1>
          <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-500'} mt-1`}>
            {tribunal} {uf !== '-' ? `· ${uf}` : ''} {grau !== '-' ? `· ${grau}` : ''}
          </p>
        </div>
      </div>

      {/* Informações do Processo */}
      <div className={`${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
        <div className="flex items-center gap-2 mb-6">
          <FileText className={darkMode ? 'text-primary-400' : 'text-primary-600'} />
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Informações do Processo
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
          {infoItem(<Building />, 'Tribunal', textVal(tribunal, darkMode ? 'text-cyan-400' : 'text-cyan-700'), darkMode ? 'text-cyan-400' : 'text-cyan-600')}
          {infoItem(<MapPin />, 'UF', textVal(uf), darkMode ? 'text-green-400' : 'text-green-600')}
          {infoItem(<MapPin />, 'Comarca / Cidade', textVal(comarca), darkMode ? 'text-teal-400' : 'text-teal-600')}
          {infoItem(<Scale />, 'Classe', textVal(classe), darkMode ? 'text-primary-400' : 'text-primary-600')}
          {infoItem(<FileText />, 'Assunto', textVal(assunto), darkMode ? 'text-purple-400' : 'text-purple-600')}
          {infoItem(<FileText />, 'Área', textVal(area), darkMode ? 'text-indigo-400' : 'text-indigo-600')}
          {infoItem(<Flag />, 'Polo', (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
              polo === 'ATIVO'
                ? darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-100 text-primary-700'
                : darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'
            }`}>
              {tipoEnvolvimento !== '-' ? tipoEnvolvimento : polo}
            </span>
          ), darkMode ? 'text-indigo-400' : 'text-indigo-600')}
          {infoItem(<Calendar />, 'Data Início', textVal(formatDate(processo.data_inicio)), darkMode ? 'text-orange-400' : 'text-orange-600')}
          {infoItem(<Calendar />, 'Última Movimentação', textVal(formatDate(processo.data_ultima_movimentacao)), darkMode ? 'text-yellow-400' : 'text-yellow-600')}
          {infoItem(<Activity />, 'Status', (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
              isAtivo
                ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                : darkMode ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-100 text-gray-700'
            }`}>
              {isAtivo ? 'Ativo' : 'Arquivado'}
            </span>
          ), darkMode ? 'text-pink-400' : 'text-pink-600')}
          {infoItem(<Gavel />, 'Órgão Julgador', textVal(orgaoJulgador), darkMode ? 'text-amber-400' : 'text-amber-600')}
          {infoItem(<Gavel />, 'Juiz / Relator', textVal(juiz), darkMode ? 'text-amber-400' : 'text-amber-600')}

          {/* Valor da Causa (sem borda inferior, span completo) */}
          <div className="py-5">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              <p className={`text-xs font-semibold ${darkMode ? 'text-neutral-400' : 'text-gray-600'} uppercase`}>Valor da Causa</p>
            </div>
            <p className={`text-base font-bold ${darkMode ? 'text-green-400' : 'text-green-600'} ml-6`}>
              {valorFormatado}
            </p>
          </div>
        </div>

        {/* Links para o tribunal */}
        {mainFonte?.url && (
          <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
            <a
              href={mainFonte.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 text-sm font-medium ${
                darkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-800'
              } transition-colors`}
            >
              <ExternalLink className="w-4 h-4" />
              Consultar no tribunal ({tribunal})
            </a>
          </div>
        )}
      </div>

      {/* Envolvidos */}
      <div className={`${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
        <div className="flex items-center gap-2 mb-6">
          <Users className={darkMode ? 'text-primary-400' : 'text-primary-600'} />
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Envolvidos
          </h2>
          <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
            darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-600'
          }`}>
            {envolvidos.length}
          </span>
        </div>

        {envolvidos.length === 0 ? (
          <div className="text-center py-12">
            <Users className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-neutral-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
              Nenhum envolvido encontrado
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Ativo */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-primary-500"></div>
                <h3 className="text-lg font-semibold text-primary-500">Ativo</h3>
                <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
                  darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-100 text-primary-700'
                }`}>
                  {envolvidos.filter(e => e.polo === 'ATIVO').length}
                </span>
              </div>
              <div className="space-y-3">
                {envolvidos.filter(e => e.polo === 'ATIVO').length > 0
                  ? envolvidos.filter(e => e.polo === 'ATIVO').map((env, i) => (
                    <EnvolvidoCard
                      key={i}
                      envolvido={env}
                      darkMode={darkMode}
                      onEnriquecer={isCPF(env.cpf_cnpj) && env.tipo?.toLowerCase() !== 'advogado' ? () => obterEnriquecimento(env) : null}
                    />
                  ))
                  : <p className={`text-sm text-center py-8 ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>Nenhum envolvido ativo</p>
                }
              </div>
            </div>

            {/* Coluna Passivo */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <h3 className="text-lg font-semibold text-purple-500">Passivo</h3>
                <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
                  darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'
                }`}>
                  {envolvidos.filter(e => e.polo === 'PASSIVO').length}
                </span>
              </div>
              <div className="space-y-3">
                {envolvidos.filter(e => e.polo === 'PASSIVO').length > 0
                  ? envolvidos.filter(e => e.polo === 'PASSIVO').map((env, i) => (
                    <EnvolvidoCard
                      key={i}
                      envolvido={env}
                      darkMode={darkMode}
                      onEnriquecer={isCPF(env.cpf_cnpj) && env.tipo?.toLowerCase() !== 'advogado' ? () => obterEnriquecimento(env) : null}
                    />
                  ))
                  : <p className={`text-sm text-center py-8 ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>Nenhum envolvido passivo</p>
                }
              </div>
            </div>

            {/* Outros polos (ADVOGADO, DESCONHECIDO, etc.) */}
            {envolvidos.filter(e => e.polo !== 'ATIVO' && e.polo !== 'PASSIVO').length > 0 && (
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>Outros</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {envolvidos.filter(e => e.polo !== 'ATIVO' && e.polo !== 'PASSIVO').map((env, i) => (
                    <EnvolvidoCard key={i} envolvido={env} darkMode={darkMode} onEnriquecer={null} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Movimentações */}
      <div className={`${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden`}>
        <button
          onClick={handleVerMovimentacoes}
          disabled={movimentacoesLoading}
          className={`w-full flex items-center justify-between p-6 transition-colors ${
            darkMode ? 'hover:bg-neutral-800' : 'hover:bg-gray-50'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <div className="flex items-center gap-2">
            <FileText className={darkMode ? 'text-primary-400' : 'text-primary-600'} />
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Movimentações
            </h2>
            <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
              darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-600'
            }`}>
              {processo.quantidade_movimentacoes || 0}
            </span>
          </div>
          {movimentacoesLoading
            ? <Loader2 className="w-5 h-5 animate-spin" />
            : mostrarMovimentacoes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />
          }
        </button>

        {mostrarMovimentacoes && (
          <div className={`p-6 border-t ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
            {movimentacoes?.movimentacoes?.length > 0 ? (
              <>
                <div className="space-y-4 mb-4">
                  {(() => {
                    const start = (movimentacaoCurrentPage - 1) * movimentacaoPageSize;
                    const slice = movimentacoes.movimentacoes.slice(start, start + movimentacaoPageSize);

                    return slice.map((mov, i) => {
                      const titulo = mov.classificacao_predita?.nome || mov.tipo_publicacao || mov.tipo || 'Movimentação';
                      const isPublicacao = mov.tipo === 'PUBLICACAO';
                      return (
                        <div key={mov.id ?? i} className={`p-4 rounded-lg border ${darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {titulo}
                              </p>
                              <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                                <span>{mov.data ? new Date(mov.data + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}</span>
                                {mov.fonte?.sigla && <span className={`font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>{mov.fonte.sigla}</span>}
                                {mov.fonte?.grau_formatado && <span>{mov.fonte.grau_formatado}</span>}
                              </div>
                            </div>
                            <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded font-medium ${
                              isPublicacao
                                ? darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'
                                : darkMode ? 'bg-neutral-700 text-neutral-400' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {isPublicacao ? 'Publicação' : 'Andamento'}
                            </span>
                          </div>
                          {mov.conteudo && (
                            <p className={`text-sm ${darkMode ? 'text-neutral-300' : 'text-gray-700'} leading-relaxed whitespace-pre-line`}>
                              {mov.conteudo}
                            </p>
                          )}
                          {mov.classificacao_predita?.descricao && (
                            <p className={`text-xs mt-2 italic ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                              {mov.classificacao_predita.descricao}
                            </p>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Paginação movimentações */}
                {Math.ceil(movimentacoes.movimentacoes.length / movimentacaoPageSize) > 1 && (
                  <div className={`flex items-center justify-between px-4 py-4 border-t ${darkMode ? 'bg-neutral-800/50 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      <label className={`text-sm font-medium ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Itens por página:</label>
                      <select
                        value={movimentacaoPageSize}
                        onChange={(e) => { setMovimentacaoPageSize(parseInt(e.target.value)); setMovimentacaoCurrentPage(1); }}
                        className={`px-3 py-1 rounded border text-sm ${darkMode ? 'bg-neutral-700 border-neutral-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none`}
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                      Página {movimentacaoCurrentPage} de {Math.ceil(movimentacoes.movimentacoes.length / movimentacaoPageSize)}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setMovimentacaoCurrentPage(p => p - 1)}
                        disabled={movimentacaoCurrentPage === 1}
                        className={`px-3 py-1 rounded border text-sm ${movimentacaoCurrentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-neutral-700 border-neutral-600 text-neutral-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setMovimentacaoCurrentPage(p => p + 1)}
                        disabled={movimentacaoCurrentPage === Math.ceil(movimentacoes.movimentacoes.length / movimentacaoPageSize)}
                        className={`px-3 py-1 rounded border text-sm ${movimentacaoCurrentPage === Math.ceil(movimentacoes.movimentacoes.length / movimentacaoPageSize) ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'bg-neutral-700 border-neutral-600 text-neutral-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {movimentacoes?.nextLink && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={handleCarregarMaisMovimentacoes}
                      disabled={movimentacoesLoadingMore}
                      className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:bg-neutral-700 disabled:opacity-50'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                      }`}
                    >
                      {movimentacoesLoadingMore
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</>
                        : 'Carregar mais'
                      }
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className={`text-center py-8 ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                Nenhuma movimentação encontrada
              </p>
            )}
          </div>
        )}
      </div>

      {/* Modal de Enriquecimento */}
      <ModalEnriquecimento
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEnvolvidoSelecionado(null); setDadosEnriquecidos(null); setEnriquecimentoError(null); }}
        envolvido={envolvidoSelecionado}
        dados={dadosEnriquecidos}
        loading={enriquecimentoLoading}
        error={enriquecimentoError}
        darkMode={darkMode}
      />
    </div>
  );
}

function EnvolvidoCard({ envolvido, darkMode, onEnriquecer }) {
  return (
    <div className={`p-4 rounded-lg border ${darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`}>{envolvido.nome}</p>
          <p className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>{envolvido.tipo}</p>
          {envolvido.cpf_cnpj && (
            <p className={`text-xs font-mono ${darkMode ? 'text-neutral-400' : 'text-gray-500'} mt-1`}>
              {envolvido.cpf_cnpj}
            </p>
          )}
          {envolvido.oabs?.length > 0 && (
            <p className={`text-xs ${darkMode ? 'text-primary-400' : 'text-primary-600'} mt-1`}>
              OAB: {envolvido.oabs.map(o => `${o.uf} ${o.numero}`).join(', ')}
            </p>
          )}
          {envolvido.quantidade_processos > 0 && (
            <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'} mt-1`}>
              {envolvido.quantidade_processos} processos cadastrados
            </p>
          )}
        </div>
        {onEnriquecer && (
          <button
            onClick={onEnriquecer}
            className={`flex-shrink-0 p-2 rounded transition-colors ${
              darkMode ? 'hover:bg-neutral-700 text-neutral-400 hover:text-primary-400' : 'hover:bg-gray-100 text-gray-600 hover:text-primary-600'
            }`}
            title="Buscar processos deste envolvido"
          >
            <Search className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
